import { startTransition } from 'react';
import { AppState } from 'src/AppState';
import { reportError } from 'src/easyclaim.app.libs/reportError';
import { ServiceError } from 'src/easyclaim.app.services';

import App from '../App';
import {
	ProductType,
	EasyClaimFrontendStep,
	EventBehaviorType,
	EventType,
	EasyClaimAppState,
} from '../easyclaim.api.client.typescript/src';

type EventBehaviorStrategy = () => void | Promise<void>;

export class AppNavigator {
	private readonly _appRef: App;
	private readonly scrollToTopCallback?: () => void;

	constructor(appRef: App, scrollToTopCallback: () => void) {
		this._appRef = appRef;
		this.scrollToTopCallback = scrollToTopCallback;
	}

	get appState() {
		return this._appRef.state;
	}

	public onNext = async () => {
		this._appRef.setState((prev) => ({ ...prev, isExecutingAction: true }));
		const currentFrontendStep = this.getCurrentFrontendStep();

		try {
			if (
				currentFrontendStep?.eventConfigurations?.some(
					(ec) => ec.eventType === EventType.OnNext
				) === true
			) {
				const onNextEventConfiguration = currentFrontendStep.eventConfigurations.find(
					(ec) => ec.eventType === EventType.OnNext
				);

				if (
					onNextEventConfiguration?.eventBehaviors &&
					onNextEventConfiguration.eventBehaviors.length
				) {
					for (const eventBehavior of onNextEventConfiguration.eventBehaviors) {
						if (eventBehavior.eventBehaviorType) {
							const eventBehaviorStrategy = this.getEventBehaviorStrategy(
								eventBehavior.eventBehaviorType
							);

							if (eventBehaviorStrategy) {
								await eventBehaviorStrategy();
							}
						}
					}
				}
			}
		} catch (e: unknown) {
			const serviceError = e as ServiceError;

			if (serviceError.responseStatus === 409) {
				const conflictError: ServiceError = {
					responseStatus: 409,
					responseStatusText: 'Entity was modified on some other device.',
					details: [],
				};
				const serviceErrors = [conflictError];

				this._appRef.setState((prev) => ({ ...prev, serviceErrors, isExecutingAction: false }));
				// modal z info
			} else {
				reportError(e, {
					context: 'AppNavigator.onNext',
					screen: this._appRef.state.screen,
				});
				this._appRef.setState((prev) => ({
					...prev,
					serviceErrors: (prev.serviceErrors || []).concat([serviceError]),
					isExecutingAction: false,
				}));
			}

			return;
		}

		const nextState = this.getNextState();

		startTransition(() => {
			this._appRef.setState((prev) => ({
				...prev,
				screen: prev.reviewReturnScreen ?? nextState,
				reviewReturnScreen: undefined,
				reviewImageComponentId: undefined,
				completeModalOpen: false,
				completeModalItems: [],
				mismatchModalOpen: false,
				mismatchModalImages: [],
				isExecutingAction: false,
				isMileageMismatchModalOpen: false,
				isMileageMismatchModalConfirmValue: false,
				isMileageDirty: false,
			}));
		});

		this.scrollToTopCallback?.();
	};

	public onSkip = async (state: AppState): Promise<void> => {
		const CurrentStateScreenRenderer = state.appService.getCurrentStateScreenRenderer();

		CurrentStateScreenRenderer!.onSkip!(state);
		await this.onNext();
	};

	public getCurrentFrontendStep = (): EasyClaimFrontendStep | null => {
		if (
			!this.appState.easyClaimCase ||
			!this.appState.easyClaimCase._configuration ||
			!this.appState.easyClaimCase._configuration.frontendSteps
		) {
			return null;
		}

		const frontendSteps = this.appState.easyClaimCase._configuration.frontendSteps;
		const steps = frontendSteps.map((s) => s.appState!);
		const stepIndex = steps.indexOf(this.appState.screen!);

		const step = steps.length > stepIndex ? frontendSteps[stepIndex] : null;

		return step;
	};

	private getNextState = (): EasyClaimAppState | undefined => {
		if (
			!this.appState.easyClaimCase ||
			!this.appState.easyClaimCase._configuration ||
			!this.appState.easyClaimCase._configuration.frontendSteps
		) {
			return undefined;
		}
		const steps = this.appState.easyClaimCase._configuration.frontendSteps.map((s) => s.appState!);
		const nextStepIndex = steps.indexOf(this.appState.screen!) + 1;
		const nextStep = steps.length > nextStepIndex ? steps[nextStepIndex] : undefined;

		return nextStep;
	};

	private getEventBehaviorStrategy = (
		eventBehaviorType: EventBehaviorType
	): EventBehaviorStrategy | null => {
		switch (eventBehaviorType) {
			case EventBehaviorType.ConfirmPicturesUploaded:
				return this.appState.appService.confirmPicturesUploaded;

			case EventBehaviorType.ChangeDamageDescription:
				return this.appState.appService.updateDamage;

			case EventBehaviorType.SendFeedback:
				return this.appState.appService.sendFeedback;

			case EventBehaviorType.SendOfferFeedback:
				return this.appState.appService.sendOfferFeedback;

			case EventBehaviorType.RedirectToAnotherPage: {
				const redirectLink = this.appState.easyClaimCase?.finishUploadRedirectLink;

				if (!redirectLink) {
					return null;
				}

				return () => window.location.replace(redirectLink);
			}
			case EventBehaviorType.SendQuestionsAnswers:
				return this.appState.appService.sendQuestionsAnswers;
			default:
				return null;
		}
	};

	public onPrev = () => {
		if (
			!this.appState.easyClaimCase ||
			!this.appState.easyClaimCase._configuration ||
			!this.appState.easyClaimCase._configuration.frontendSteps
		) {
			return;
		}
		const steps = this.appState.easyClaimCase._configuration.frontendSteps.map((s) => s.appState!);
		const newStateProps = {
			screen: steps[steps.indexOf(this.appState.screen!) - 1],
			reviewImageComponentId: undefined,
		};

		startTransition(() => {
			void this.appState.setAppState({ ...this.appState, ...newStateProps });
		});
	};

	public canGoBack = (): boolean => {
		if (this.appState.easyClaimCase?._configuration?.layout === ProductType.PolicyCheck) {
			return (
				this.appState.screen === EasyClaimAppState.VehicleInformation ||
				this.appState.screen === EasyClaimAppState.FourAdditionalPictures ||
				this.appState.screen === EasyClaimAppState.FourAdditionalPicturesAZCEE ||
				this.appState.screen === EasyClaimAppState.FourAdditionalDocuments ||
				this.appState.screen === EasyClaimAppState.VehicleRegistrationDocument
			);
		}

		if (
			!this.appState.easyClaimCase ||
			!this.appState.easyClaimCase._configuration ||
			!this.appState.easyClaimCase._configuration.frontendSteps
		) {
			return false;
		}
		const step = this.appState.easyClaimCase._configuration.frontendSteps.find(
			(fs) => fs.appState === this.appState.screen
		);
		const order = step?.order ?? -1;

		return order > 1;
	};
}
